import { PdfViewer } from "@/components/common/PdfViewer/PdfViewer"
import { Button } from "@/components/ui/button"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { cn } from "@/lib/utils"
import { downloadFileFromUrl } from "@/utils/downloadUtils"
import { Download, FileText, Loader2, X } from "lucide-react"
import { useEffect, useId, useState, type ReactNode } from "react"
import { toastApiError } from "../../../api/apiError"
import type {
  TimelineEmail,
  TimelineEmailAttachment,
} from "../../../api/supplierInquiriesApi"
import { formatDate } from "../../../utils/date"
import { splitReferenceToken } from "../../../utils/referenceToken"
import { McPill } from "../../shared/McPill"
import { SectionLabel } from "../../shared/SectionLabel"

interface InquiryEmailSheetProps {
  email: TimelineEmail | null
  open: boolean
  onOpenChange: (open: boolean) => void
  // Inquiry-level recipients — fallback for outbound rows that predate per-email storage.
  recipientsTo?: string[]
  recipientsCc?: string[]
}

/** A file is previewable inline only if it's a PDF. Everything else downloads. */
function isPdf(att: TimelineEmailAttachment): boolean {
  const type = att.contentType?.toLowerCase() ?? ""
  const name = att.filename?.toLowerCase() ?? ""
  return type.includes("pdf") || name.endsWith(".pdf")
}

function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-3">
      <dt className="w-16 shrink-0 text-grey5">{label}</dt>
      <dd className="min-w-0 wrap-break-word text-grey6">{value}</dd>
    </div>
  )
}

// Collapse a long body to a preview + Show more/less. Clamps by HEIGHT only (no content
// parsing), so it's client/language-agnostic and never hides anything.
const BODY_PREVIEW_LINES = 10
const BODY_PREVIEW_CHARS = 600

// Render an email body, grouping consecutive quoted lines (the `>`-prefixed reply
// history that mail clients append) into a styled blockquote. This is a display-only
// pass over the standard plain-text quoting convention — one leading `>` is stripped
// per block, so nested quotes keep their remaining markers.
function renderEmailBody(text: string): ReactNode {
  const blocks: { quoted: boolean; lines: string[] }[] = []
  for (const line of text.split("\n")) {
    const quoted = /^\s*>/.test(line)
    const last = blocks[blocks.length - 1]
    if (last && last.quoted === quoted) last.lines.push(line)
    else blocks.push({ quoted, lines: [line] })
  }
  return blocks.map((block, i) =>
    block.quoted ? (
      <blockquote
        key={i}
        className="my-1 border-l-2 border-grey4 pl-3 text-grey5"
      >
        {block.lines.map((l) => l.replace(/^\s*>\s?/, "")).join("\n")}
      </blockquote>
    ) : (
      <span key={i}>{block.lines.join("\n")}</span>
    ),
  )
}

export function CollapsibleBody({
  text,
  textClassName,
}: {
  text: string
  textClassName?: string
}) {
  const { translate } = useTranslation()
  const [expanded, setExpanded] = useState(false)
  const bodyId = useId()
  // Reset when the body changes (a different email opened / a different thread row).
  useEffect(() => {
    setExpanded(false)
  }, [text])

  const isLong =
    text.split("\n").length > BODY_PREVIEW_LINES ||
    text.length > BODY_PREVIEW_CHARS

  return (
    <>
      <div
        id={bodyId}
        className={cn(
          "whitespace-pre-wrap",
          isLong && !expanded && "max-h-40 overflow-hidden",
          textClassName,
        )}
      >
        {renderEmailBody(text)}
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          aria-controls={bodyId}
          className="mt-1 text-[11.5px] font-medium text-grey5 hover:text-white"
        >
          {translate(
            expanded ? "MC_INQ_EMAIL_SHOW_LESS" : "MC_INQ_EMAIL_SHOW_MORE",
          )}
        </button>
      )}
    </>
  )
}

export function InquiryEmailSheet({
  email,
  open,
  onOpenChange,
  recipientsTo,
  recipientsCc,
}: InquiryEmailSheetProps) {
  const { translate } = useTranslation()
  const [preview, setPreview] = useState<TimelineEmailAttachment | null>(null)

  // Collapse any open preview whenever the email changes (e.g. a different
  // timeline step opens the sheet) so we never show a stale file.
  useEffect(() => {
    setPreview(null)
  }, [email])

  const handleOpenChange = (next: boolean) => {
    if (!next) setPreview(null)
    onOpenChange(next)
  }

  // Which attachment is being fetched, if any. The download presigns and
  // streams the file, so the click had no visible effect until the browser
  // opened it — the tester pressed it three times in three seconds (F21).
  const [downloading, setDownloading] = useState<string | null>(null)

  const handleDownload = async (att: TimelineEmailAttachment) => {
    if (!att.fileUrl || downloading) return
    setDownloading(att.fileUrl)
    try {
      await downloadFileFromUrl(att.fileUrl, att.filename ?? "attachment")
    } catch (error) {
      toastApiError(error, translate, "MC_API_ERROR_GENERIC")
    } finally {
      setDownloading(null)
    }
  }

  const isInbound = email?.direction === "inbound"
  const attachments = email?.attachments ?? []
  // Show a clean subject + a discrete reference chip instead of the raw
  // backend-appended "[Ref: ...]" tag inline in the subject line.
  const { subject, reference } = splitReferenceToken(email?.subject)

  // To/Cc: prefer the email's own parsed lists (now present for inbound + outbound).
  // Only outbound legacy rows without lists fall back to the inquiry's recipients, then
  // the single `recipient`.
  const resolvedTo = email?.recipientsTo?.length
    ? email.recipientsTo
    : !isInbound
      ? recipientsTo
      : undefined
  const resolvedCc = email?.recipientsCc?.length
    ? email.recipientsCc
    : !isInbound
      ? recipientsCc
      : undefined
  const toDisplay = resolvedTo?.length
    ? resolvedTo.join(", ")
    : (email?.recipient ?? "—")
  const ccDisplay = resolvedCc?.length ? resolvedCc.join(", ") : ""

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        className={cn(
          "flex gap-0 p-0",
          preview ? "w-[85vw] sm:max-w-[1080px]" : "w-full sm:max-w-md",
        )}
      >
        {preview && (
          <div className="flex min-w-0 flex-1 flex-col border-r border-grey4 bg-grey1">
            <div className="flex h-12 shrink-0 items-center gap-2 border-b border-grey4 px-4">
              <FileText className="h-4 w-4 shrink-0 text-grey5" />
              <span className="min-w-0 flex-1 truncate text-[13px] text-white">
                {preview.filename}
              </span>
              <button
                type="button"
                onClick={() => setPreview(null)}
                className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-[12px] text-grey5 hover:bg-grey3 hover:text-white"
              >
                {translate("MC_INQ_EMAIL_CLOSE_PREVIEW")}
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
            <div className="min-h-0 flex-1">
              <PdfViewer
                fileUrl={preview.fileUrl ?? undefined}
                showControls={false}
                className="h-full"
              />
            </div>
          </div>
        )}

        <div
          className={cn(
            "flex min-h-0 flex-col",
            preview ? "w-[400px] shrink-0" : "w-full",
          )}
        >
          {email && (
            <>
              <SheetHeader className="space-y-2 border-b border-grey4 p-5 pr-12 text-left">
                <McPill
                  className="self-start"
                  tone={isInbound ? "success" : "info"}
                >
                  {translate(
                    isInbound
                      ? "MC_INQ_EMAIL_INBOUND"
                      : "MC_INQ_EMAIL_OUTBOUND",
                  )}
                </McPill>
                <SheetTitle className="text-base leading-snug text-white">
                  {subject || translate("MC_INQ_EMAIL_NO_SUBJECT")}
                </SheetTitle>
                {reference && (
                  <p className="text-[11.5px] text-grey5">
                    {translate("MC_INQ_COL_REFERENCE")}:{" "}
                    <span className="font-mono text-grey6">{reference}</span>
                  </p>
                )}
              </SheetHeader>

              <div className="min-h-0 flex-1 overflow-y-auto p-5">
                <dl className="space-y-2 text-[13px]">
                  <MetaRow
                    label={translate("MC_INQ_EMAIL_FROM")}
                    value={email.sender ?? "—"}
                  />
                  <MetaRow
                    label={translate("MC_INQ_EMAIL_TO")}
                    value={toDisplay}
                  />
                  {ccDisplay && (
                    <MetaRow
                      label={translate("MC_INQ_EMAIL_CC")}
                      value={ccDisplay}
                    />
                  )}
                  <MetaRow
                    label={translate("MC_INQ_EMAIL_DATE")}
                    value={formatDate(email.sentAt) || "—"}
                  />
                </dl>

                {email.body && (
                  <div className="mt-4 border-t border-grey4 pt-4">
                    <CollapsibleBody
                      text={email.body}
                      textClassName="text-[13px] leading-relaxed text-grey6"
                    />
                  </div>
                )}

                <div className="mt-4 border-t border-grey4 pt-4">
                  <SectionLabel className="mb-2.5">
                    {translate("MC_INQ_EMAIL_ATTACHMENTS")} (
                    {attachments.length})
                  </SectionLabel>
                  {attachments.length === 0 ? (
                    <p className="text-[12.5px] text-grey5">
                      {translate("MC_INQ_EMAIL_NO_ATTACHMENTS")}
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {attachments.map((att, i) => {
                        const active =
                          preview?.s3Key === att.s3Key &&
                          preview?.filename === att.filename
                        const previewable = isPdf(att) && !!att.fileUrl
                        return (
                          <li key={att.s3Key ?? `${att.filename}-${i}`}>
                            <div
                              role={previewable ? "button" : undefined}
                              tabIndex={previewable ? 0 : undefined}
                              onClick={
                                previewable ? () => setPreview(att) : undefined
                              }
                              onKeyDown={
                                previewable
                                  ? (e) => {
                                      if (e.key === "Enter" || e.key === " ") {
                                        e.preventDefault()
                                        setPreview(att)
                                      }
                                    }
                                  : undefined
                              }
                              className={cn(
                                "flex items-center gap-3 rounded-lg border bg-grey2 px-3 py-2.5",
                                active ? "border-yellow1" : "border-grey4",
                                previewable &&
                                  "cursor-pointer hover:border-grey5",
                              )}
                            >
                              <FileText className="h-4 w-4 shrink-0 text-grey5" />
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-[13px] text-white">
                                  {att.filename ?? "—"}
                                </p>
                                {att.contentType && (
                                  <p className="truncate text-[11px] text-grey5">
                                    {att.contentType}
                                  </p>
                                )}
                              </div>
                              {att.fileUrl && (
                                <Button
                                  size="icon"
                                  variant="ghost"
                                  className="h-8 w-8 shrink-0 text-grey5"
                                  aria-label={translate(
                                    "MC_INQ_EMAIL_DOWNLOAD",
                                  )}
                                  // handleDownload early-returns while ANY download is in flight, so
                                  // leaving the others enabled gave a click that did nothing.
                                  disabled={downloading !== null}
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    void handleDownload(att)
                                  }}
                                >
                                  {downloading === att.fileUrl ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Download className="h-4 w-4" />
                                  )}
                                </Button>
                              )}
                            </div>
                          </li>
                        )
                      })}
                    </ul>
                  )}
                </div>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  )
}
